import { Add, Close } from '@mui/icons-material';
import { Box, Button, Fade, IconButton, InputAdornment, Typography } from '@mui/material';
import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import { Renter } from '../../../utils/types/renter.interface';
import { RenterInviteTextInput } from '../components';
import { theme } from '@theguarantors/tg-package-material-ui';
import { useInviteTracking } from '../hooks';
import { InviteSegmentEvents } from '../constants';
import { getApplicantEmailErrorMessage, getApplicantEmailValidationResult } from '../utils';

interface Props {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  trigger: UseFormTrigger<Renter>;
}

export const RenterContact = ({ control, errors, watch, trigger }: Props) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'applicantEmails',
  });
  const { trackInvite } = useInviteTracking();

  const applicantEmails = watch('applicantEmails');
  const errorMsgForApplicantEmail = (index: number) =>
    getApplicantEmailErrorMessage(errors, applicantEmails, index);
  const getValidationResult = (index: number, isFieldValid: boolean) =>
    getApplicantEmailValidationResult(applicantEmails, index, isFieldValid);

  return (
    <>
      <Typography
        variant="h6"
        component="h2"
        fontWeight="bold !important"
        mb={1}
        sx={{
          lineHeight: '28px',
          [theme.breakpoints.down('md')]: {
            fontSize: '16px',
            lineHeight: '24px',
          },
        }}
      >
        Applicant contact information
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontSize: '16px',
          [theme.breakpoints.down('md')]: {
            fontSize: '14px',
            lineHeight: '22px',
          },
        }}
        fontWeight="350"
        mb={2}
        letterSpacing={0}
      >
        Enter your applicant's email address below. You can add up to five additional applicants, if
        applicable.
      </Typography>
      <Controller
        name="applicantEmails.0.value"
        control={control}
        render={({ field }) => (
          <RenterInviteTextInput
            {...field}
            ref={null}
            size="small"
            id="client-email-address"
            data-testid="client-email-address"
            label="Primary Applicant Email Address"
            required
            autoComplete="section-renter email"
            error={!!errorMsgForApplicantEmail(0)}
            helperText={errorMsgForApplicantEmail(0)}
            sx={{ mt: 0 }}
            onBlur={async () => {
              field.onBlur();
              const isFieldValid = await trigger('applicantEmails.0.value');
              trackInvite(InviteSegmentEvents.EmailValidationCompleted, {
                field_name: 'applicantEmails.0.value',
                applicant_index: 0,
                validation_result: getValidationResult(0, isFieldValid),
                validation_source: 'client',
              });
            }}
            slotProps={{
              input: {
                classes: {
                  root: 'fs-mask',
                },
              },
            }}
          />
        )}
      />
      {fields.map(
        (roommate: { id: string }, index: number) =>
          index > 0 && (
            <Fade in={index > 0} key={roommate.id}>
              <Box sx={{ mt: 2 }}>
                <Controller
                  name={`applicantEmails.${index}.value`}
                  key={roommate.id}
                  control={control}
                  render={({ field }) => (
                    <RenterInviteTextInput
                      {...field}
                      ref={null}
                      size="small"
                      id={`applicant-emails-${index}`}
                      data-testid={`applicant-emails-${index}`}
                      label={`Additional Applicant Email Address`}
                      required
                      autoComplete={`section-roommate-${index} email`}
                      error={!!errorMsgForApplicantEmail(index)}
                      helperText={errorMsgForApplicantEmail(index)}
                      onBlur={async () => {
                        field.onBlur();
                        const isFieldValid = await trigger(`applicantEmails.${index}.value`);
                        trackInvite(InviteSegmentEvents.EmailValidationCompleted, {
                          field_name: `applicantEmails.${index}.value`,
                          applicant_index: index,
                          validation_result: getValidationResult(index, isFieldValid),
                          validation_source: 'client',
                        });
                      }}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="remove applicant"
                                onClick={() => {
                                  remove(index);
                                  trackInvite(InviteSegmentEvents.AdditionalApplicantChanged, {
                                    action: 'remove',
                                    applicant_count: fields.length - 1,
                                    applicant_index: index,
                                  });
                                }}
                                edge="end"
                              >
                                <Close />
                              </IconButton>
                            </InputAdornment>
                          ),
                          disableUnderline: true,
                          classes: {
                            root: 'fs-mask',
                          },
                        },
                      }}
                    />
                  )}
                />
              </Box>
            </Fade>
          ),
      )}
      {fields.length <= 5 && (
        <Button
          variant="outlined"
          onClick={() => {
            append(
              { value: '' },
              { shouldFocus: true, focusName: `applicantEmails.${fields.length}.value` },
            );
            trackInvite(InviteSegmentEvents.AdditionalApplicantChanged, {
              action: 'add',
              applicant_count: fields.length + 1,
              applicant_index: fields.length,
            });
          }}
          id="add-roommate-button"
          data-testid="add-roommate-button"
          sx={{
            mt: 2,
            padding: '12px',
            pl: 2,
            pr: 3,
            gap: 1,
            borderColor: `${theme.palette.neutralGrey?.[200] ?? theme.palette.grey[200]} !important`,
            [theme.breakpoints.down('md')]: {
              width: 'fit-content',
            },
          }}
        >
          <Add />
          Add additional applicant
        </Button>
      )}
    </>
  );
};
